import React, { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { checkSession, sessionProblem, subscribeSession } from '../../services/sessionRecovery';

export function SessionRecovery({ children }: { children: React.ReactNode }) {
  const [problem, setProblem] = useState(sessionProblem);
  const [checking, setChecking] = useState(false);
  useEffect(() => subscribeSession(() => setProblem(sessionProblem())), []);
  useEffect(() => {
    if (!problem) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [problem]);
  useEffect(() => {
    const verify = () => { if (document.visibilityState === 'visible') void checkSession(); };
    window.addEventListener('focus', verify);
    document.addEventListener('visibilitychange', verify);
    const interval = window.setInterval(verify, 60000);
    return () => { window.removeEventListener('focus', verify); document.removeEventListener('visibilitychange', verify); clearInterval(interval); };
  }, []);
  const retry = async () => { setChecking(true); try { await checkSession(); } finally { setChecking(false); } };
  return <>
    <div hidden={!!problem}>{children}</div>
    <Dialog open={!!problem} onClose={() => {}} className="relative z-[100]">
      <div className="fixed inset-0 bg-slate-950/70" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
        <Dialog.Panel className="w-full max-w-lg space-y-4 rounded-xl bg-white p-6 text-slate-900 shadow-xl">
          <Dialog.Title className="text-xl font-semibold">{problem === 'connection' ? 'Connection interrupted' : problem === 'different_account' ? 'A different account is signed in' : 'Sign in to continue'}</Dialog.Title>
          <Dialog.Description>{problem === 'connection' ? 'We cannot confirm your connection to the classroom right now. This may happen during an update or a network interruption.' : problem === 'different_account' ? 'This page belongs to the account that started this work. Sign back in with that same school account before continuing.' : 'Your session has ended. Sign back in with the same school account to continue.'}</Dialog.Description>
          <p>Your unfinished work is still open in this tab. Keep this tab open. Nothing will be submitted automatically.</p>
          <div className="flex flex-wrap gap-3">
            {problem !== 'connection' && <a className="rounded-lg bg-purple-800 px-4 py-2 text-white" href="/login?session_recovery=1" target="_blank" rel="noopener noreferrer">Sign in in a new tab</a>}
            <button className="rounded-lg border border-slate-300 px-4 py-2 disabled:opacity-50" disabled={checking} onClick={() => void retry()}>{checking ? 'Checking…' : problem === 'connection' ? 'Check connection' : 'I signed in — continue'}</button>
          </div>
          <p className="text-sm text-slate-600">When you return, review your work and select Save or Submit again.</p>
        </Dialog.Panel>
      </div>
    </Dialog>
  </>;
}
